import { CHECKPOINTS, DEMO_FINDINGS, DEMO_PIPELINE, type DemoFindingFixture } from "./demoData";

// The demo report reproduces the BackdoorTech penetration-test report template —
// the same document the real backend renders to PDF — but built in the browser
// from the sample findings and the host the user typed during the product tour.
// It is a single self-contained HTML file (fonts and artwork inlined as data:
// URIs), so the download opens offline and the sandboxed preview iframe passes
// the production CSP without any third-party request.

const SEV_LABEL: Record<Sev, string> = {
  critical: "CRITICAL",
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",
};
type Sev = "critical" | "high" | "medium" | "low";
const SEV_ORDER: Sev[] = ["critical", "high", "medium", "low"];
const SEV_WHAT: Record<Sev, string> = {
  critical: "Exploitable right now, serious impact",
  high: "Takes effort but leads to data compromise",
  medium: "Amplifies other attacks, discloses information",
  low: "Useful to an attacker, no direct impact",
};

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c);

const para = (s: string): string =>
  esc(s)
    .split("\n")
    .map((line) => `<p>${line}</p>`)
    .join("");

/** A short, deterministic report identifier derived from the host, formatted
 *  like a UUID so the document control block reads like the real one. */
function reportId(host: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < host.length; i++) {
    h ^= host.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (n: number, len: number) => (n >>> 0).toString(16).padStart(8, "0").slice(0, len);
  const a = hex(h, 8);
  const b = hex(Math.imul(h, 7) ^ 0x9e37, 4);
  const c = hex(Math.imul(h, 13) ^ 0x79b9, 4);
  const d = hex(Math.imul(h, 17) ^ 0x7f4a, 4);
  const e = hex(Math.imul(h, 31) ^ 0x6c62, 8) + hex(Math.imul(h, 5), 4);
  return `${a}-${b}-${c}-${d}-${e}`;
}

/** CVSS exploitability → likelihood bucket (0 Low · 1 Medium · 2 High). */
function likelihood(cvss: string): 0 | 1 | 2 {
  const ac = /AC:([LH])/.exec(cvss)?.[1];
  const pr = /PR:([NLH])/.exec(cvss)?.[1];
  const ui = /UI:([NR])/.exec(cvss)?.[1];
  if (ac === "H" || pr === "H") return 0;
  if (ac === "L" && pr === "N" && ui === "N") return 2;
  return 1;
}

/** CVSS impact sub-score (count of High in C/I/A) → impact bucket. */
function impact(cvss: string): 0 | 1 | 2 {
  const highs = (cvss.match(/[CIA]:H/g) ?? []).length;
  return highs >= 2 ? 2 : highs === 1 ? 1 : 0;
}

/** A plain-language "what this means for the business" line per finding. */
function businessImpact(f: DemoFindingFixture): string {
  switch (f.severity) {
    case "critical":
      return "Customer data is exposed to an unauthenticated visitor. This is the kind of flaw that drives breach notification, regulatory fines and loss of customer trust.";
    case "high":
      return "An authenticated or low-effort attacker can reach data that should be out of scope for them, widening any breach and the notification obligations that follow.";
    case "medium":
      return "On its own the impact is limited, but it hands an attacker a foothold that combines with the higher-severity findings into a fuller compromise.";
    case "low":
      return "No direct impact was proven. It is hygiene: left in place it slowly raises the odds that a future change turns it into something exploitable.";
  }
}

interface Page {
  /** Visible heading used to resolve the table of contents. */
  label?: string;
  html: string;
}

/** Build the report as a self-contained A4 HTML document. */
export async function demoReportHtml(host: string): Promise<string> {
  const { demoReportAssets: A } = await import("./demoReportAssets");

  const safeHost = esc(host);
  const counts = DEMO_FINDINGS.reduce<Record<string, number>>((acc, f) => {
    acc[f.severity] = (acc[f.severity] ?? 0) + 1;
    return acc;
  }, {});
  const total = DEMO_FINDINGS.length;
  const critical = counts.critical ?? 0;
  const high = counts.high ?? 0;
  const immediate = critical + high;
  const maxScore = Math.max(...DEMO_FINDINGS.map((f) => f.score));
  const worst = SEV_ORDER.find((s) => (counts[s] ?? 0) > 0) ?? "low";
  const overall = worst === "critical" ? "CRITICAL RISK" : worst === "high" ? "HIGH RISK" : worst === "medium" ? "MEDIUM RISK" : "LOW RISK";

  const isoDate = new Date().toISOString().slice(0, 10);
  const lastMin = CHECKPOINTS[CHECKPOINTS.length - 1].elapsedMin;
  const duration = `${Math.floor(lastMin / 60)}h ${String(lastMin % 60).padStart(2, "0")}m`;
  const rid = reportId(host);

  // Findings in severity order (critical → low), numbered F-001…
  const items = SEV_ORDER.flatMap((sev) => DEMO_FINDINGS.filter((f) => f.severity === sev)).map((f, i) => ({
    f,
    id: `F-${String(i + 1).padStart(3, "0")}`,
    num: i + 1,
  }));

  // ── Reusable fragments ───────────────────────────────────────────────────
  const brandHeader = `<header class="ph"><img class="ph-mark" src="${A.wordmark}" alt="BackDoor" height="15">${A.fox}</header>`;
  const footer = (n: number, totalPages: number) =>
    `<footer class="pf"><span>${esc(rid)} · ${safeHost}</span><span>CONFIDENTIAL · page ${n} of ${totalPages}</span></footer>`;

  const stat = (n: number, label: string) =>
    `<div class="stat"><div class="stat-num">${n}</div><div class="stat-lbl">${label}</div></div>`;

  // Risk matrix: 3 likelihood rows × 3 impact columns, each finding placed once.
  const cellIds: string[][][] = [0, 1, 2].map(() => [0, 1, 2].map(() => [] as string[]));
  for (const it of items) {
    const row = 2 - likelihood(it.f.cvss); // row 0 = High likelihood
    const col = impact(it.f.cvss); // col 0 = Low impact
    cellIds[row][col].push(it.id);
  }
  const mxTone = (row: number, col: number): string => {
    const score = (2 - row) + col; // 0..4, higher = worse
    return score >= 3 ? "crit" : score === 2 ? "high" : score === 1 ? "med" : "low";
  };
  const mxCell = (row: number, col: number): string => {
    const ids = cellIds[row][col];
    return `<div class="mx-cell mx-${mxTone(row, col)}"><div class="mx-n">${ids.length}</div><div class="mx-ids">${ids.join(" ")}</div></div>`;
  };

  // ── Pages ────────────────────────────────────────────────────────────────
  const pages: Page[] = [];

  // 1 · Cover
  pages.push({
    html: `<section class="page page--cover">
      <header class="cover-top"><img class="cover-mark" src="${A.wordmark}" alt="BackDoor" height="22">${A.fox}</header>
      <div class="cover-art" style="background-image:url(${A.cover})"></div>
      <div class="cover-date">${esc(isoDate)}</div>
      <h1 class="cover-h">COMPREHENSIVE<br>PENETRATION TEST</h1>
      <div class="cover-conf">CONFIDENTIAL</div>
    </section>`,
  });

  // 2 · Document control
  pages.push({
    label: "Document control",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Document control</h2>
      <div class="note">
        <div class="note-h">Confidentiality notice</div>
        This report contains a detailed description of vulnerabilities in the ${safeHost} information system, including how they can be exploited. The document is classified CONFIDENTIAL and is intended solely for authorised representatives of the client. Unauthorised distribution, copying or disclosure to third parties is prohibited and may create additional risk to the system until the described weaknesses are remediated.
      </div>
      <h3 class="h-sub">Project parameters</h3>
      <table class="params">
        <tr><th>Target system</th><td>${safeHost}</td></tr>
        <tr><th>Test type</th><td>Automated external testing (black-box)</td></tr>
        <tr><th>Methodology</th><td>OWASP Top-10, OWASP API Security Top-10</td></tr>
        <tr><th>Assessment date</th><td>${esc(isoDate)}</td></tr>
        <tr><th>Scan duration</th><td>${esc(duration)}</td></tr>
        <tr><th>Performed by</th><td>BackdoorTech</td></tr>
        <tr><th>Pipeline</th><td>${esc(DEMO_PIPELINE.name)} v${esc(DEMO_PIPELINE.version)}</td></tr>
        <tr><th>Report identifier</th><td>${esc(rid)}</td></tr>
        <tr><th>Classification</th><td>CONFIDENTIAL</td></tr>
      </table>
      <h3 class="h-sub">How to read this report</h3>
      <p>The document is split into two parts. Part I «For management» is written in plain language and explains the business problems and risks the findings create and what to do first. Part II «Technical part» describes each vulnerability in detail with evidence and remediation guidance. All findings are prioritised by severity.</p>
      <div class="callout">Important. Potential-impact estimates are forward-looking and are provided to help prioritise remediation. The assessor is not a legal or financial advisor; a final assessment of regulatory risk should be made together with the client's own specialists.</div>
    </section>`,
  });

  // 3 · Table of contents (filled after the page numbers are known)
  const tocIndex = pages.length;
  pages.push({ html: "" });

  // 4 · Part I divider
  pages.push({
    html: `<section class="page page--divider">
      <div class="divider-kicker">PART I</div>
      <h1 class="divider-h">FOR MANAGEMENT</h1>
      <p class="divider-sub">A short, plain-language account of the business problems, risks and immediate actions. No technical background required.</p>
    </section>`,
  });

  // 5 · Executive summary
  const execRisks = items
    .filter((it) => it.f.severity === "critical" || it.f.severity === "high")
    .map((it) => `<li><b>${esc(it.f.title)}.</b> ${esc(businessImpact(it.f))}</li>`)
    .join("");
  pages.push({
    label: "Executive summary",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Executive summary</h2>
      <div class="verdict verdict--${worst}">Overall Security Status: ${overall}</div>
      <p class="exec-lead">${total} findings across the web app and API · highest CVSS ${maxScore.toFixed(1)}</p>
      <p>The assessment of https://${safeHost} identified ${total} issues across the public web application and its API. The most serious is a SQL injection in the product catalogue that exposes customer data to an unauthenticated visitor; combined with an access-control gap on order details, an attacker could reach the full order history without signing in. All findings were confirmed by the agent and reviewed by a human analyst.</p>
      <h3 class="h-sub">Key business risks</h3>
      <ul class="risks">${execRisks}</ul>
      <h3 class="h-sub">Strategic recommendations</h3>
      <ul class="risks">
        <li>Fix the critical and high findings before the next release; they are the ones reachable with little or no authentication.</li>
        <li>Treat any data the SQL injection could read as potentially exposed, and follow your breach-assessment process with your own specialists.</li>
        <li>Add the missing authorisation and output-encoding checks to the endpoints' test suites so the same class of bug cannot ship again.</li>
      </ul>
      <div class="stats">
        ${stat(total, "Total vulnerabilities")}
        ${stat(critical, "Critical")}
        ${stat(high, "High")}
        ${stat(immediate, "Require immediate action")}
      </div>
    </section>`,
  });

  // 6 · Risk matrix
  pages.push({
    label: "Risk matrix",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Risk matrix</h2>
      <p class="muted">Findings mapped by CVSS exploitability (likelihood) and impact sub-score (impact) · n = ${total}</p>
      <div class="mx-axis-x">IMPACT →</div>
      <div class="matrix">
        <div class="mx-corner">LIKELIHOOD ↓</div>
        <div class="mx-head">Low</div><div class="mx-head">Medium</div><div class="mx-head">High</div>
        <div class="mx-row">High</div>${mxCell(0, 0)}${mxCell(0, 1)}${mxCell(0, 2)}
        <div class="mx-row">Medium</div>${mxCell(1, 0)}${mxCell(1, 1)}${mxCell(1, 2)}
        <div class="mx-row">Low</div>${mxCell(2, 0)}${mxCell(2, 1)}${mxCell(2, 2)}
      </div>
      <p class="muted">Likelihood ← CVSS v3.1 exploitability (AV/AC/PR/UI); impact ← CVSS impact sub-score (C/I/A). Low/Medium/High thresholds are an assessor-defined methodology choice; the colour scale is non-normative.</p>
      <h3 class="h-sub">Severity distribution</h3>
      <table class="dist">
        <tr><th>Severity</th><th>Count</th><th>% of total</th><th>What it means</th></tr>
        ${SEV_ORDER.map((s) => {
          const n = counts[s] ?? 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return `<tr><td><span class="dot dot--${s}"></span>${s[0].toUpperCase() + s.slice(1)}</td><td>${n}</td><td>${pct}%</td><td>${SEV_WHAT[s]}</td></tr>`;
        }).join("")}
        <tr class="dist-total"><td>Total</td><td>${total}</td><td>100%</td><td></td></tr>
      </table>
    </section>`,
  });

  // 7 · What could happen — business risks
  const bizRows = items
    .filter((it) => it.f.severity === "critical" || it.f.severity === "high")
    .map(
      (it, i) => `<div class="risk-card risk-card--${it.f.severity}">
        <div class="risk-badge">RISK ${i + 1}</div>
        <div class="risk-title">${esc(it.f.title)}</div>
        <p>${esc(it.f.description(host))}</p>
        <div class="risk-conseq">Consequences</div>
        <p>${esc(businessImpact(it.f))}</p>
      </div>`,
    )
    .join("");
  pages.push({
    label: "What could happen",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">What could happen</h2>
      <p class="muted">The business problems the findings create, in plain language.</p>
      ${bizRows}
    </section>`,
  });

  // 8 · Immediate actions
  const actions = items
    .filter((it) => it.f.severity === "critical" || it.f.severity === "high")
    .map((it, i) => ({
      action: it.f.remediation.replace(/^\s*/, "").split(/(?<=\.)\s/)[0],
      why: `${SEV_LABEL[it.f.severity]} · ${esc(it.f.title)} (${it.f.path}). ${esc(businessImpact(it.f))}`,
      n: i + 1,
    }));
  pages.push({
    label: "Immediate actions",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Immediate actions</h2>
      <p class="muted">First steps, in priority order, to take the sharpest risk off the table.</p>
      <table class="actions">
        <tr><th>#</th><th>Action</th><th>Why it matters</th></tr>
        ${actions.map((a) => `<tr><td>${a.n}</td><td>${esc(a.action)}</td><td>${a.why}</td></tr>`).join("")}
      </table>
    </section>`,
  });

  // 9 · Part II divider
  pages.push({
    html: `<section class="page page--divider">
      <div class="divider-kicker">PART II</div>
      <h1 class="divider-h">TECHNICAL PART</h1>
      <p class="divider-sub">A detailed description of each vulnerability with evidence and remediation. Findings are prioritised by severity.</p>
    </section>`,
  });

  // 10 · Findings summary table
  pages.push({
    label: "Findings summary table",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Findings summary table</h2>
      <table class="summary">
        <tr><th>ID</th><th>Severity</th><th>Vulnerability</th><th>CVSS</th><th>Endpoint</th></tr>
        ${items
          .map(
            (it) => `<tr>
              <td class="mono">${it.id}</td>
              <td><span class="sev sev--${it.f.severity}">${SEV_LABEL[it.f.severity]}</span></td>
              <td>${esc(it.f.title)}</td>
              <td class="mono">${it.f.score.toFixed(1)}</td>
              <td class="mono">https://${safeHost}${esc(it.f.path)}</td>
            </tr>`,
          )
          .join("")}
      </table>
    </section>`,
  });

  // 11… · One page per finding
  for (const it of items) {
    const f = it.f;
    pages.push({
      label: f.title,
      html: `<section class="page">${brandHeader}
        <article class="finding finding--${f.severity}" id="${it.id}">
          <div class="f-head">
            <span class="f-id mono">${it.id}</span>
            <span class="sev sev--${f.severity}">${SEV_LABEL[f.severity]}</span>
            <h3 class="f-title">${esc(f.title)}</h3>
            <span class="cvss-chip">CVSS ${esc(f.score.toFixed(1))}</span>
          </div>
          <table class="meta">
            <tr><th>Target</th><td class="mono">https://${safeHost}${esc(f.path)}</td></tr>
            <tr><th>Type</th><td>${esc(f.vuln_type)}</td></tr>
            <tr><th>CVSS</th><td class="mono">${esc(f.score.toFixed(1))} · ${esc(f.cvss)}</td></tr>
            <tr><th>Tool</th><td>${esc(f.tool_name)}</td></tr>
            <tr><th>Evidence level</th><td>Confirmed (proof)</td></tr>
          </table>
          <p class="tldr"><b>TL;DR.</b> ${esc(f.description(host).split(/(?<=\.)\s/)[0])}</p>
          <h4>Description</h4>
          ${para(f.description(host))}
          <div class="why"><div class="why-h">Why it matters</div>${para(businessImpact(f))}</div>
          <h4>Evidence · proof of concept</h4>
          ${para(f.poc(host))}
          <h4>Remediation</h4>
          ${para(f.remediation)}
        </article>
      </section>`,
    });
  }

  // last · Remediation roadmap
  const byPrio = (label: string, sevs: Sev[]) => {
    const rows = items.filter((it) => sevs.includes(it.f.severity));
    if (!rows.length) return "";
    return `<div class="road-band">${label}</div>${rows
      .map((it) => `<div class="road-row"><span class="mono">${it.id}</span> ${esc(it.f.remediation.split(/(?<=\.)\s/)[0])}</div>`)
      .join("")}`;
  };
  pages.push({
    label: "Remediation roadmap",
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Remediation roadmap</h2>
      <p class="muted">Recommended remediation order by priority. P0 clears critical risks, P1 closes high, P2 covers medium and low.</p>
      ${byPrio("P0 · Immediate — 24–48 hours", ["critical"])}
      ${byPrio("P1 · Short-term — 1–2 weeks", ["high"])}
      ${byPrio("P2 · Mid-term — up to 1 month", ["medium", "low"])}
      <div class="road-end">Potential-impact estimates are forward-looking and are provided to help prioritise remediation.<br>Report generated by BackdoorTech scan orchestration.</div>
    </section>`,
  });

  const totalPages = pages.length;

  // Table of contents — resolve each labelled page to its number.
  const tocRows = pages
    .map((p, i) => ({ p, n: i + 1 }))
    .filter((r) => r.p.label)
    .map(
      (r) =>
        `<div class="toc-row" data-toc="${esc(r.p.label as string)}" data-page="${r.n}"><span class="toc-label">${esc(r.p.label as string)}</span><span class="toc-dots"></span><span class="toc-page">${r.n}</span></div>`,
    )
    .join("");
  pages[tocIndex] = {
    html: `<section class="page">${brandHeader}
      <h2 class="h-title">Table of contents</h2>
      <div class="toc">${tocRows}</div>
    </section>`,
  };

  const body = pages
    .map((p, i) => {
      // Cover and dividers carry no running header/footer.
      if (p.html.includes("page--cover") || p.html.includes("page--divider")) return p.html;
      return p.html.replace("</section>", `${footer(i + 1, totalPages)}</section>`);
    })
    .join("\n");

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<style>
  @font-face { font-family: "Unbounded"; src: url(${A.unbounded}) format("woff2"); font-weight: 400 800; font-display: swap; }
  @font-face { font-family: "ReportMono"; src: url(${A.robotoMono}) format("woff2"); font-weight: 400 700; font-display: swap; }
  :root {
    --ink: #1a1a1a; --muted: #6b6b6b; --line: #e7e7e7;
    --brand: #c5fb4c; --dark: #0b0b0b;
    --crit: #b91c1c; --high: #dc2626; --med: #d97706; --low: #65a30d;
  }
  * { box-sizing: border-box; }
  @page { size: A4; margin: 0; }
  body { margin: 0; background: #2b2b2b; color: var(--ink);
    font-family: "ReportMono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10.5px; line-height: 1.55; }
  .page { position: relative; width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff;
    padding: 16mm 15mm 20mm; overflow: hidden; }
  @media print { .page { margin: 0; page-break-after: always; } }
  h1, h2, h3, h4, .cover-h, .divider-h, .f-title, .risk-title, .mx-n, .stat-num { font-family: "Unbounded", "ReportMono", sans-serif; }
  .h-title { font-size: 19px; font-weight: 700; margin: 0 0 10px; letter-spacing: -0.3px; text-transform: uppercase; }
  .exec-lead { color: var(--muted); margin: -4px 0 8px; }
  .h-sub { font-size: 12px; font-weight: 600; margin: 16px 0 6px; }
  h4 { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--muted); margin: 11px 0 3px; font-family: "ReportMono", monospace; }
  p { margin: 0 0 6px; }
  .muted { color: var(--muted); }
  .mono { font-family: "ReportMono", monospace; }

  /* Running header / footer */
  .ph { position: absolute; top: 0; left: 0; right: 0; height: 13mm; background: var(--dark);
    display: flex; align-items: center; justify-content: space-between; padding: 0 15mm; }
  .ph-mark { display: block; }
  .ph svg { height: 18px; width: auto; }
  .pf { position: absolute; left: 15mm; right: 15mm; bottom: 10mm; display: flex; justify-content: space-between;
    font-size: 7.5px; color: #9a9a9a; border-top: 1px solid var(--line); padding-top: 2mm; }
  .page:not(.page--cover):not(.page--divider) { padding-top: 22mm; }

  /* Cover */
  .page--cover { background: var(--dark); color: #fff; padding: 0; }
  .cover-top { position: absolute; top: 0; left: 0; right: 0; height: 16mm; z-index: 3;
    display: flex; align-items: center; justify-content: space-between; padding: 0 14mm;
    border-bottom: 1px solid rgba(255,255,255,0.12); }
  .cover-top svg { height: 26px; }
  .cover-art { position: absolute; inset: 0; background-size: cover; background-position: center right; }
  .cover-art::after { content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, #0b0b0b 0%, rgba(11,11,11,0.82) 32%, rgba(11,11,11,0.1) 72%, rgba(11,11,11,0.45) 100%); }
  .cover-date { position: absolute; top: 46%; left: 10mm; z-index: 3; writing-mode: vertical-rl;
    transform: rotate(180deg); font-size: 8px; letter-spacing: 3px; color: rgba(255,255,255,0.6); }
  .cover-h { position: absolute; top: 40%; left: 18mm; right: 18mm; z-index: 3; margin: 0;
    font-size: 40px; font-weight: 700; line-height: 1.04; letter-spacing: -1px; }
  .cover-conf { position: absolute; left: 14mm; right: 14mm; bottom: 14mm; z-index: 3;
    border: 1px solid var(--crit); color: var(--crit); text-align: center; padding: 8px;
    letter-spacing: 4px; font-size: 11px; }

  /* Dividers */
  .page--divider { background: var(--dark); color: #fff; display: flex; flex-direction: column; justify-content: center; }
  .divider-kicker { font-size: 10px; letter-spacing: 5px; color: rgba(255,255,255,0.55); margin-bottom: 8px; }
  .divider-h { font-size: 44px; font-weight: 700; margin: 0 0 10px; letter-spacing: -1px; }
  .divider-sub { max-width: 360px; color: rgba(255,255,255,0.7); }

  /* Document control */
  .note { border: 1px solid var(--ink); border-radius: 10px; padding: 11px 13px; margin: 2px 0 6px; }
  .note-h { font-weight: 700; margin-bottom: 4px; }
  .params { border-collapse: collapse; width: 100%; margin: 4px 0; }
  .params th, .params td { text-align: left; padding: 5px 8px; border-bottom: 1px solid var(--line); vertical-align: top; }
  .params th { width: 40mm; color: var(--muted); font-weight: 600; }
  .callout { background: #fff6ed; border-left: 3px solid var(--med); padding: 8px 11px; margin-top: 8px; color: #7a5a2a; }

  /* TOC */
  .toc { margin-top: 6px; }
  .toc-row { display: flex; align-items: baseline; gap: 6px; padding: 6px 0; border-bottom: 1px solid var(--line); }
  .toc-label { white-space: nowrap; }
  .toc-dots { flex: 1; border-bottom: 1px dotted #cfcfcf; transform: translateY(-3px); }
  .toc-page { color: var(--muted); }

  /* Executive summary */
  .verdict { display: inline-block; font-weight: 700; padding: 4px 10px; border-radius: 5px; margin: 2px 0 8px; }
  .verdict--critical { background: #fdeaea; color: var(--crit); }
  .verdict--high { background: #fdecec; color: var(--high); }
  .verdict--medium { background: #fff6ed; color: var(--med); }
  .verdict--low { background: #f3fae8; color: var(--low); }
  ul.risks { margin: 4px 0; padding-left: 16px; }
  ul.risks li { margin-bottom: 5px; }
  .stats { display: flex; gap: 8px; margin-top: 14px; }
  .stat { flex: 1; border: 1px solid var(--line); border-radius: 10px; padding: 10px; text-align: center; }
  .stat-num { font-size: 26px; font-weight: 800; line-height: 1; }
  .stat-lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.6px; color: var(--muted); margin-top: 4px; }

  /* Risk matrix */
  .mx-axis-x { color: var(--muted); font-size: 8px; letter-spacing: 1px; margin: 4px 0 2px 24mm; }
  .matrix { display: grid; grid-template-columns: 24mm 1fr 1fr 1fr; grid-auto-rows: 20mm; gap: 4px; margin: 0 0 8px; align-items: stretch; }
  .mx-corner, .mx-head, .mx-row { display: flex; align-items: center; justify-content: center; color: var(--muted); font-size: 9px; }
  .mx-corner { font-size: 7.5px; letter-spacing: 0.5px; writing-mode: horizontal-tb; }
  .matrix > .mx-corner, .matrix > .mx-head { align-self: end; min-height: 0; }
  .matrix { grid-template-rows: auto 20mm 20mm 20mm; }
  .mx-cell { border-radius: 8px; padding: 8px; color: #fff; min-height: 20mm; display: flex; flex-direction: column; justify-content: space-between; }
  .mx-cell.mx-crit { background: var(--crit); } .mx-cell.mx-high { background: var(--high); }
  .mx-cell.mx-med { background: var(--med); } .mx-cell.mx-low { background: var(--low); }
  .mx-n { font-size: 22px; font-weight: 800; line-height: 1; }
  .mx-ids { font-size: 7.5px; opacity: 0.92; word-break: break-word; }
  .dist { border-collapse: collapse; width: 100%; }
  .dist th, .dist td { text-align: left; padding: 5px 8px; border-bottom: 1px solid var(--line); }
  .dist th { color: var(--muted); font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; }
  .dist-total td { font-weight: 700; border-top: 2px solid var(--ink); }
  .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
  .dot--critical { background: var(--crit); } .dot--high { background: var(--high); }
  .dot--medium { background: var(--med); } .dot--low { background: var(--low); }

  /* Business risks */
  .risk-card { border: 1px solid var(--line); border-left: 3px solid var(--line); border-radius: 10px; padding: 10px 13px; margin-bottom: 9px; }
  .risk-card--critical { border-left-color: var(--crit); }
  .risk-card--high { border-left-color: var(--high); }
  .risk-badge { display: inline-block; background: var(--dark); color: #fff; font-size: 8px; font-weight: 700; letter-spacing: 1px; padding: 2px 7px; border-radius: 4px; }
  .risk-title { font-weight: 700; font-size: 12px; margin: 5px 0; }
  .risk-conseq { color: var(--crit); font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }

  /* Immediate actions + summary + roadmap tables */
  .actions, .summary { border-collapse: collapse; width: 100%; }
  .actions th, .actions td, .summary th, .summary td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--line); vertical-align: top; }
  .actions th, .summary th { color: var(--muted); font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; }
  .actions td:first-child { width: 8mm; font-weight: 700; }
  .summary td.mono { word-break: break-all; }

  /* Severity chips */
  .sev { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8px; font-weight: 700; color: #fff; }
  .sev--critical { background: var(--crit); } .sev--high { background: var(--high); }
  .sev--medium { background: var(--med); } .sev--low { background: var(--low); }

  /* Finding */
  .finding { border-left: 3px solid var(--line); padding-left: 10px; }
  .finding--critical { border-left-color: var(--crit); }
  .finding--high { border-left-color: var(--high); }
  .finding--medium { border-left-color: var(--med); }
  .finding--low { border-left-color: var(--low); }
  .f-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
  .f-id { font-weight: 700; }
  .f-title { font-size: 14px; font-weight: 700; margin: 0; flex: 1 1 60%; letter-spacing: -0.2px; }
  .cvss-chip { font-size: 8px; font-weight: 700; color: #555; background: #f1f1ee; border-radius: 4px; padding: 2px 6px; }
  .meta { border-collapse: collapse; width: 100%; margin: 4px 0; }
  .meta th, .meta td { text-align: left; padding: 4px 8px; border-bottom: 1px solid var(--line); vertical-align: top; }
  .meta th { width: 28mm; color: var(--muted); font-weight: 600; }
  .meta td.mono { word-break: break-all; }
  .tldr { background: #f6f7f4; border-radius: 6px; padding: 7px 10px; margin: 6px 0; }
  .why { background: #fff6ed; border-left: 3px solid var(--med); border-radius: 0 8px 8px 0; padding: 8px 11px; margin: 8px 0; color: #7a5a2a; }
  .why-h { font-weight: 700; margin-bottom: 3px; }

  /* Roadmap */
  .road-band { background: var(--dark); color: #fff; font-weight: 700; padding: 6px 10px; border-radius: 6px; margin: 10px 0 4px; }
  .road-row { padding: 5px 8px; border-bottom: 1px solid var(--line); }
  .road-end { margin-top: 14px; padding-top: 6px; border-top: 1px solid var(--line); font-size: 8px; color: var(--muted); }
</style></head>
<body>
${body}
</body></html>`;
}
